import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import { ICallAPIFunction } from "../../yoello-lib/modules/API2/types";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import EColourPanel from "../../yoello-lib/modules/dynamicForms/interfaces/EColourPanel";
import { useSelector } from "react-redux";
import { IReduxAppState } from "../../reducers";
import { IDialogRouteChildren } from "../dialogRoutes/DialogRoute";
import { useChangedDialog } from "../../hooks/useChangedDialog";
import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";
import { groupForm } from "../../formSchemas/group.form";
import { ILanguage } from "../../yoello-lib/modules/redux/actions/localization";
import { IVenueGroup } from "./state/groupsManagement.reducer";
import { useVenues } from "./useVenues";
import { generateErrorMessage } from "./util/generateErrorMessage";

export interface IGroupFormData {
  name_public:string;
  description:string;
  nickname:string;
  venues:{id:string , venue_nickname:string}[];
  language_id:string;
}
interface IGroupFormDialog extends IDialogRouteChildren {
  callGroupsAPI: ICallAPIFunction<IVenueGroup[] | IVenueGroup>;
  groupId: string;
  groupFormData?: IGroupFormData;
  setGroupsListItems:() => void;
}

export function GroupFormDialog({
  onClose,
  callGroupsAPI,
  onDialogClose,
  setGroupsListItems
}: IGroupFormDialog) {
  const [isLoading, setIsLoading] = useState(false);
  const languages = useSelector(
    (state: IReduxAppState) => state.general.languages
  ) as ILanguage[];
  const { t } = useTranslation();
  const { hasFormChanged } = useChangedDialog();
  const { sendSnackbarMessage } = useSnackbarMessages()!;
  const { venues, isLoading:isVenuesLoading , setSearchText } = useVenues();

  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.DISCOUNT_AND_PROMOTIONS);


  async function onSubmit(groupData:Partial<IGroupFormData>) {
    try {
      setIsLoading(true);
      const { data , isSuccessful} = await callGroupsAPI(HTTPMethods.POST, {
        body: {
          name_public:groupData.name_public,
          description:groupData.description,
          nickname:groupData.nickname,
          language_id:groupData.language_id
        }
      });
      if (isSuccessful && data ) {
        const { id } = data as IVenueGroup;
        const result = await callGroupsAPI(HTTPMethods.POST, {
          query: `${id}/venues/attach_detach`,
          body: {
            venue_group_id: id,
            venue_ids:groupData.venues?.map((venue) => venue.id)
          }
        });
        if(result.isSuccessful){
          sendSnackbarMessage(t("snackbar.updateSuccess"), "success");
        }else{
          sendSnackbarMessage(t(generateErrorMessage(result.data)), 'error');
        }
        setGroupsListItems();
        onDialogClose?.();
      }else{
        sendSnackbarMessage(t(generateErrorMessage(data)), 'error');
        setIsLoading(false);
      }
    } catch (error) {
      sendSnackbarMessage(error as string, 'error');
      setIsLoading(false);
    }
  }
  return (
    <DynamicForm
      useFormOptions={{
        mode: "onChange", reValidateMode: "onChange"
      }}
      onHandleSubmit={onSubmit}
      formRows={groupForm(t , venues, languages, isVenuesLoading, setSearchText)}
      disableAll={!hasPermission}
      disableAllInteraction={onFailedInteraction}
      formKeyName={"Group"}
      isDialog={true}
      hasDataChanged={hasFormChanged}
      formButtons={{
        leftButton: {
          type: "button",
          label: t("forms.buttons.cancel"),
          color: EColourPanel.DEFAULT,
          fullWidth: false,
          onClick: onClose,
        },
        rightButton: {
          type: "submit",
          label: t("discounts.buttons.updateDiscount"),
          color: EColourPanel.PRIMARY,
          fullWidth: false,
          disabled: isLoading || !hasPermission,
        },
      }}
    />
  );
}


